/**
 * ============================================================================
 * OP ID CLIENT - the canonical suite auth client (SUITE-AUTH-PLAN.md AMEND-4)
 * ============================================================================
 * THIS FILE IS THE THING EVERY OP APP COPIES. AMEND-4 lock #3: if what apps copy
 * still reads the raw session cookie, app #2 inherits the shortcut and the
 * broker bought nothing. Copy this file verbatim; only `createOpId()` options
 * differ per app.
 *
 * WHAT CHANGED VS THE OLD PATTERN
 * The previous reference client (@supabase/ssr cookie storage, and OPSetlist's
 * hand-rolled cookie reader) had each app reading - and sometimes rewriting -
 * the shared `sb-<ref>-auth-token` cookie. That coupled every app to
 * @supabase/ssr's exact chunking/encoding, and let several apps refresh the SAME
 * refresh token, which Supabase's reuse-detection treats as theft (it revokes
 * the whole family = silent logout everywhere).
 *
 * Now: `accounts.overpoweredapps.net` owns and refreshes the master session.
 * Apps call `GET /api/session` and get a short-lived access token of their own.
 * No app parses a cookie. No app refreshes. The collision class is gone by
 * construction, not by careful coding.
 *
 * PLAIN JAVASCRIPT ON PURPOSE - the suite mixes TS (home, accounts, OPX) and
 * vanilla JS (OPSetlist, OPButtons). A dependency-free ES module drops into both.
 * Types are in the sibling `opid-client.d.ts`.
 *
 * WHAT IT GIVES YOU
 *   - getToken()      fresh access token, or null. Dedupes concurrent callers.
 *   - getIdentity()   { sub, email, username, tier } from the verified JWT.
 *   - signOut()       device-wide: global revoke + local clear + front-channel.
 *   - subscribe(fn)   state changes ('signed-in' | 'signed-out' | 'offline' |
 *                     'switched').
 *   - Offline unlock  cached JWT verified locally against cached JWKS (AMEND-1),
 *                     usable for `graceDays` past expiry. STRICT: unverifiable
 *                     credentials are refused, never downgraded.
 *   - Account-switch guard (AMEND-4 addendum B): if the master session's `sub`
 *                     changes, we emit 'switched' so the app can drop the
 *                     previous user's local data.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   - Sign-in UI. Send users to the hub, or embed your own email+code form
 *     against Supabase directly; either way the master session lands in the
 *     cookie and this client picks it up on the next getToken().
 *   - Touch the master cookie. Ever.
 */

const DEFAULT_BROKER = 'https://accounts.overpoweredapps.net'

// Refetch this far before expiry. Comfortably inside the broker's own 120s
// refresh window so we normally get a still-fresh token without forcing work.
const REFETCH_SKEW_MS = 60 * 1000

/**
 * @param {Object} [options]
 * @param {string} [options.broker]     Broker origin. Default accounts.overpoweredapps.net.
 * @param {number} [options.graceDays]  Offline grace past token expiry. Default 30 (AMEND-1).
 * @param {string} [options.appId]      Namespaces localStorage so two apps on one
 *                                      origin don't clobber each other. Default 'op'.
 * @param {boolean} [options.revalidateOnFocus] Re-check the broker when the tab
 *                                      regains focus. Default true - this is what
 *                                      makes a remote sign-out propagate in seconds.
 */
export function createOpId(options = {}) {
  const broker = (options.broker || DEFAULT_BROKER).replace(/\/+$/, '')
  const graceMs = (options.graceDays ?? 30) * 24 * 60 * 60 * 1000
  const appId = options.appId || 'op'
  const revalidateOnFocus = options.revalidateOnFocus !== false

  const CACHE_KEY = `${appId}-opid-session-v1`
  const JWKS_KEY = `${appId}-opid-jwks-v1`

  /** @type {{access_token: string, expires_at: number, user: {id?: string, email?: string|null}|null}|null} */
  let current = null
  /** @type {Promise<string|null>|null} */
  let inFlight = null
  let lastSub = null
  let offlineMode = false
  const listeners = new Set()

  // ── storage helpers (never throw - Safari private mode, quota, etc.) ────────
  const store = {
    get(k) {
      try {
        return localStorage.getItem(k)
      } catch {
        return null
      }
    },
    set(k, v) {
      try {
        localStorage.setItem(k, v)
      } catch {
        /* best effort */
      }
    },
    del(k) {
      try {
        localStorage.removeItem(k)
      } catch {
        /* best effort */
      }
    },
  }

  function emit(state, detail) {
    for (const fn of listeners) {
      try {
        fn(state, detail)
      } catch {
        /* a bad listener must not break auth */
      }
    }
  }

  // ── base64url / JWT ────────────────────────────────────────────────────────
  function b64urlToBytes(b64url) {
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i)
    return out
  }

  function decodeClaims(token) {
    try {
      const payload = token.split('.')[1]
      return JSON.parse(new TextDecoder().decode(b64urlToBytes(payload)))
    } catch {
      return null
    }
  }

  // ── offline: cache + STRICT local verification ─────────────────────────────
  async function cacheForOffline(token) {
    store.set(CACHE_KEY, JSON.stringify({ accessToken: token, cachedAt: Date.now() }))
    // Refresh the public keys while we have network. The broker proxies JWKS so
    // an app only ever needs ONE configured origin.
    try {
      const res = await fetch(`${broker}/api/jwks`, { credentials: 'omit' })
      if (res.ok) store.set(JWKS_KEY, await res.text())
    } catch {
      /* keep whatever we cached before */
    }
  }

  async function verifyLocally(token) {
    const jwksRaw = store.get(JWKS_KEY)
    if (!jwksRaw) return false // no cached key -> cannot verify -> refuse

    const parts = token.split('.')
    if (parts.length !== 3) return false
    const [h, p, sig] = parts

    let header
    try {
      header = JSON.parse(new TextDecoder().decode(b64urlToBytes(h)))
    } catch {
      return false
    }
    if (header.alg !== 'ES256') return false

    let jwks
    try {
      jwks = JSON.parse(jwksRaw)
    } catch {
      return false
    }
    const jwk = (jwks.keys || []).find((k) => k.kid === header.kid) || (jwks.keys || [])[0]
    if (!jwk) return false

    try {
      const key = await crypto.subtle.importKey(
        'jwk',
        jwk,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['verify'],
      )
      return await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        key,
        b64urlToBytes(sig),
        new TextEncoder().encode(`${h}.${p}`),
      )
    } catch {
      return false
    }
  }

  /**
   * The logout epoch, stamped on `.overpoweredapps.net` by the broker when the
   * user signs out. Readable with ZERO network, which is the point: it is the
   * only device-wide-logout signal that can reach an app opened offline.
   */
  function readLogoutEpoch() {
    try {
      const m = (document.cookie || '').match(/(?:^|;\s*)op-logout-epoch=(\d+)/)
      return m ? parseInt(m[1], 10) : 0
    } catch {
      return 0
    }
  }

  /**
   * Try to unlock from the cached credential with zero network.
   * STRICT (AMEND-1): an unverifiable or tampered token is refused outright -
   * we never fall back to a degraded/free session on a credential we can't trust.
   */
  async function tryOfflineUnlock() {
    const raw = store.get(CACHE_KEY)
    if (!raw) return null
    let rec
    try {
      rec = JSON.parse(raw)
    } catch {
      return null
    }
    if (!rec || typeof rec.accessToken !== 'string') return null

    // Device-wide sign-out reaches us here even with no network: a credential
    // cached BEFORE the last logout is dead, however much grace it has left.
    const epoch = readLogoutEpoch()
    if (epoch && (rec.cachedAt || 0) < epoch) {
      store.del(CACHE_KEY)
      return null
    }
    if (!(await verifyLocally(rec.accessToken))) return null

    const claims = decodeClaims(rec.accessToken)
    if (!claims) return null
    const expMs = (claims.exp || 0) * 1000
    if (Date.now() > expMs + graceMs) return null // grace lapsed

    return rec.accessToken
  }

  // ── the broker call ────────────────────────────────────────────────────────
  async function fetchFromBroker() {
    // credentials:'include' is what makes the master cookie ride along. Calls
    // from *.overpoweredapps.net to accounts. are cross-ORIGIN but SAME-SITE,
    // so the SameSite=Lax cookie is sent.
    //
    // `?app=<appId>` lets the broker record "this app is signed in on this
    // device" for the suite session manager (PROPOSAL-session-manager.md).
    // Reuses the same appId already passed to createOpId() for localStorage
    // namespacing - no new option. Omit-safe: a broker that doesn't recognise
    // the param just issues the token as it always did.
    const res = await fetch(`${broker}/api/session?app=${encodeURIComponent(appId)}`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
    if (res.status === 401) return { ok: false }
    if (!res.ok) throw new Error(`broker ${res.status}`)
    const data = await res.json()
    if (!data || typeof data.access_token !== 'string') throw new Error('broker malformed')
    return { ok: true, session: data }
  }

  function isFresh(session) {
    return session && session.expires_at * 1000 - Date.now() > REFETCH_SKEW_MS
  }

  function applySession(session) {
    const claims = decodeClaims(session.access_token) || {}
    const sub = claims.sub || (session.user && session.user.id) || null

    // Account-switch guard (AMEND-4 addendum B). A different `sub` on the master
    // session means the device switched users - the app must drop the previous
    // user's local data before rendering anything.
    const switched = lastSub !== null && sub !== null && sub !== lastSub
    lastSub = sub
    current = session
    offlineMode = false
    void cacheForOffline(session.access_token)

    if (switched) emit('switched', { sub })
    else emit('signed-in', { sub })
    return session.access_token
  }

  function clearLocal() {
    current = null
    lastSub = null
    offlineMode = false
    store.del(CACHE_KEY)
    // JWKS is public, non-identifying - keep it so a later offline unlock after
    // a fresh sign-in still works on a device that goes offline immediately.
  }

  async function resolveToken() {
    if (isFresh(current)) return current.access_token

    let result
    try {
      result = await fetchFromBroker()
    } catch {
      // Network unavailable (or the broker is down). Fall back to the cached
      // credential - this is the whole point of AMEND-1.
      const offline = await tryOfflineUnlock()
      if (offline) {
        offlineMode = true
        emit('offline', { sub: (decodeClaims(offline) || {}).sub || null })
        return offline
      }
      clearLocal()
      emit('signed-out', { reason: 'offline-no-cache' })
      return null
    }

    if (!result.ok) {
      // Authoritative: no master session. Signed out everywhere, including any
      // offline cache we were holding (AMEND-4 addendum A - a real logout must
      // clear the offline credential too).
      clearLocal()
      emit('signed-out', { reason: 'no-session' })
      return null
    }

    return applySession(result.session)
  }

  /**
   * Fresh access token, or null when signed out.
   * Concurrent callers share one in-flight request - without this, an app that
   * fires several API calls on load would stampede the broker.
   */
  async function getToken() {
    if (isFresh(current)) return current.access_token
    if (inFlight) return inFlight
    inFlight = resolveToken().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  async function getIdentity() {
    const token = await getToken()
    if (!token) return null
    const c = decodeClaims(token) || {}
    return {
      sub: c.sub || null,
      email: c.email || (current && current.user ? current.user.email : null) || null,
      username: c.username || null,
      tier: c.tier || 'free',
      offline: offlineMode,
    }
  }

  /**
   * Device-wide sign-out (AMEND-4 addendum A).
   * 1. Broker revokes the master session server-side (kills the refresh-token
   *    family) and clears the master cookie - so no app can silently refresh.
   * 2. We clear this origin's local + offline state.
   * The hub additionally front-channels every app's /logout to make it instant;
   * apps that aren't reached converge on their next getToken() (401 -> clear).
   *
   * @param {'local'|'global'} [scope] Omit for the default device-only revoke.
   *   'global' is the deliberate "sign out everywhere" action (session manager
   *   PROPOSAL-session-manager.md) - every existing caller that calls signOut()
   *   with no argument is unaffected, this is additive only.
   */
  async function signOut(scope) {
    let revoked = false
    try {
      const res = await fetch(`${broker}/api/logout`, {
        method: 'POST',
        credentials: 'include',
        ...(scope === 'global'
          ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify({ scope: 'global' }) }
          : {}),
      })
      revoked = res.ok
    } catch {
      // Offline sign-out still clears local state; the master session is revoked
      // on the next online logout or when its refresh token expires.
      revoked = false
    }
    clearLocal()
    emit('signed-out', { reason: 'user', revoked })
    return { revoked }
  }

  /**
   * Front-channel logout target. An app should expose a `/logout` route that
   * calls this - the hub loads it in a hidden iframe so this origin's
   * localStorage is cleared immediately rather than on next visit.
   */
  function handleFrontChannelLogout() {
    clearLocal()
    emit('signed-out', { reason: 'front-channel' })
  }

  function subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  }

  // Re-check on focus/online so a sign-out (or account switch) elsewhere lands
  // here within seconds instead of at the end of a token lifetime.
  if (revalidateOnFocus && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void getToken()
    })
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void getToken())
      window.addEventListener('focus', () => void getToken())
    }
  }

  return {
    getToken,
    getIdentity,
    signOut,
    subscribe,
    clearLocal,
    handleFrontChannelLogout,
    get isOffline() {
      return offlineMode
    },
    brokerOrigin: broker,
  }
}
